import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCreateUser } from '../../hooks/useUsers';
import { useSnackbar } from '../../context/SnackbarContext';

const USERNAME_RE = /^[a-z][a-z0-9_-]{2,30}$/;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateUserDialog({ open, onClose }: Props) {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const showSnackbar = useSnackbar();
  const createMutation = useCreateUser();

  const usernameError = username && !USERNAME_RE.test(username)
    ? 'Must start with a letter, 3–31 chars, lowercase letters/digits/_/-'
    : '';
  const emailError = email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Invalid email' : '';
  const canSubmit = !!username && !usernameError && !!name && !!email && !emailError &&
    (!password || password.length >= 8);

  function handleClose() {
    setUsername(''); setName(''); setEmail(''); setPassword('');
    onClose();
  }

  async function handleSubmit() {
    try {
      await createMutation.mutateAsync({ username, name, email, password: password || undefined });
      showSnackbar(`User "${username}" created`, 'success');
      handleClose();
    } catch {
      showSnackbar('Failed to create user', 'error');
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Managed User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            error={!!usernameError}
            helperText={usernameError || 'Lowercase, starts with letter, 3–31 chars'}
            required
            autoFocus
          />
          <TextField
            label="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            required
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText="Optional. Leave blank to force user to set password on first login."
            error={!!password && password.length < 8}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || createMutation.isPending}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
